import { z } from 'zod';
import { paginationMetaSchema } from '@/api/pagination';

const optionalId = z.coerce.number().int().positive().optional();
const optionalFlag = z
  .union([z.boolean(), z.literal('true'), z.literal('false')])
  .transform((value) => value === true || value === 'true')
  .optional();

/* ---------------------------------- sheet --------------------------------- */

export const requisitionSheetInstituteSchema = z.object({
  instituteId: z.coerce.number(),
  instituteCode: z.coerce.number().nullable(),
  instituteName: z.string(),
  instituteTypeName: z.string().nullable(),
  thanaName: z.string().nullable(),
  districtName: z.string().nullable(),
  divisionName: z.string().nullable(),
  requisitionId: z.coerce.number().nullable(),
  requiredQty: z.coerce.number(),
  teacherQty: z.coerce.number(),
  studentQty: z.coerce.number(),
  targetSaleQty: z.coerce.number(),
  targetDate: z.string().nullable(),
  isReview: z.boolean(),
  isApprovedLevelOne: z.boolean(),
  isApprovedLevelTwo: z.boolean(),
  reviewQty: z.coerce.number().nullable(),
  levelOneQty: z.coerce.number().nullable(),
  levelTwoQty: z.coerce.number().nullable(),
});

export const requisitionSheetSchema = z.object({
  data: z.object({
    specimenId: z.coerce.number(),
    institutes: z.array(requisitionSheetInstituteSchema),
    totals: z.object({
      required: z.coerce.number(),
      teacher: z.coerce.number(),
      targetSale: z.coerce.number(),
    }),
  }),
});

export const requisitionSheetSavedSchema = z.object({
  data: z.object({
    stored: z.coerce.number(),
    created: z.coerce.number(),
    updated: z.coerce.number(),
    locked: z.coerce.number(),
  }),
});

export const requisitionSheetSearchSchema = z.object({
  specimenId: optionalId,
  bookTypeId: optionalId,
  bookEditionId: optionalId,
  instituteTypeId: optionalId,
  thanaId: optionalId,
});

/* --------------------------------- review --------------------------------- */

export const specimenRequisitionRowSchema = z.object({
  id: z.coerce.number(),
  instituteId: z.coerce.number().nullable(),
  instituteCode: z.coerce.number().nullable(),
  instituteName: z.string().nullable(),
  instituteTypeName: z.string().nullable(),
  thanaName: z.string().nullable(),
  specimenId: z.coerce.number(),
  specimenName: z.string(),
  specimenCode: z.string().nullable(),
  requiredQty: z.coerce.number(),
  teacherQty: z.coerce.number(),
  studentQty: z.coerce.number(),
  targetSaleQty: z.coerce.number(),
  requisitionDate: z.string(),
  targetDate: z.string().nullable(),
  isReview: z.boolean(),
  isApprovedLevelOne: z.boolean(),
  isApprovedLevelTwo: z.boolean(),
  memberId: z.coerce.number(),
  memberName: z.string().nullable(),
});

export const specimenRequisitionResponseSchema = z.object({
  data: z.array(specimenRequisitionRowSchema),
  meta: paginationMetaSchema.extend({
    totals: z.object({
      required: z.coerce.number(),
      teacher: z.coerce.number(),
      targetSale: z.coerce.number(),
    }),
  }),
});

export const specimenRequisitionDecidedSchema = z.object({
  data: z.object({
    id: z.coerce.number(),
    isReview: z.boolean(),
    isApprovedLevelOne: z.boolean(),
    isApprovedLevelTwo: z.boolean(),
    reviewQty: z.coerce.number().nullable(),
    reviewTeacherQty: z.coerce.number().nullable(),
    reviewTargetSaleQty: z.coerce.number().nullable(),
    levelOneQty: z.coerce.number().nullable(),
    levelOneTargetSaleQty: z.coerce.number().nullable(),
    levelTwoQty: z.coerce.number().nullable(),
  }),
});

export const requisitionReviewSearchSchema = z.object({
  specimenId: optionalId,
  instituteTypeId: optionalId,
  thanaId: optionalId,
  isReview: optionalFlag,
  levelOne: optionalFlag,
  levelTwo: optionalFlag,
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  search: z.string().optional(),
});

export const REQUISITION_STAGES = ['REVIEW', 'LEVEL_ONE', 'LEVEL_TWO'] as const;

export type RequisitionStage = (typeof REQUISITION_STAGES)[number];

export interface DecideRequisitionPayload {
  stage: RequisitionStage;
  accept: boolean;
  qty?: number | undefined;
  teacherQty?: number | undefined;
  targetSaleQty?: number | undefined;
  targetDate?: string | undefined;
  comments?: string | undefined;
}

/* ------------------------------- promo report ------------------------------ */

export const spPromoRequisitionRowSchema = z.object({
  specimenId: z.coerce.number(),
  specimenCode: z.string(),
  specimenName: z.string(),
  specimenNameBn: z.string().nullable(),
  requiredQty: z.coerce.number(),
  approvedQty: z.coerce.number(),
  teacherQty: z.coerce.number(),
  targetSaleQty: z.coerce.number(),
});

const promoTotalsSchema = z.object({
  required: z.coerce.number(),
  approved: z.coerce.number(),
  teacher: z.coerce.number(),
  targetSale: z.coerce.number(),
});

export const spPromoRequisitionResponseSchema = z.object({
  data: z.array(spPromoRequisitionRowSchema),
  meta: paginationMetaSchema.extend({ totals: promoTotalsSchema }),
});

export const spPromoRequisitionMemberSchema = z.object({
  memberId: z.coerce.number(),
  memberName: z.string().nullable(),
  username: z.string().nullable(),
  mobile: z.string().nullable(),
  departmentName: z.string().nullable(),
  designationName: z.string().nullable(),
  divisionName: z.string().nullable(),
  districtName: z.string().nullable(),
  thanaName: z.string().nullable(),
  requiredQty: z.coerce.number(),
  approvedQty: z.coerce.number(),
  teacherQty: z.coerce.number(),
  targetSaleQty: z.coerce.number(),
});

export const spPromoRequisitionMembersSchema = z.object({
  data: z.object({
    items: z.array(spPromoRequisitionMemberSchema),
    totals: promoTotalsSchema,
  }),
});

export const spPromoRequisitionSearchSchema = z.object({
  userId: optionalId,
  specimenId: optionalId,
  specimenCode: z.string().optional(),
  districtId: optionalId,
  divisionId: optionalId,
  bookTypeId: optionalId,
  instituteId: optionalId,
  bookEditionId: optionalId,
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
});

/* ---------------------------- student collection --------------------------- */

export const studentProjectSchema = z.object({
  id: z.coerce.number(),
  code: z.string().nullable(),
  name: z.string(),
  startDate: z.string().nullable(),
  deadlineDate: z.string().nullable(),
  comments: z.string().nullable(),
  isActive: z.boolean(),
  instituteCount: z.coerce.number(),
  entryCount: z.coerce.number(),
  studentQty: z.coerce.number(),
});

export const studentProjectResponseSchema = z.object({
  data: z.array(studentProjectSchema),
  meta: paginationMetaSchema.extend({
    totals: z.object({
      entries: z.coerce.number(),
      students: z.coerce.number(),
    }),
  }),
});

export const classCategorySchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  classId: z.coerce.number(),
  className: z.string(),
  classGroup: z.string(),
  sortOrder: z.coerce.number(),
});

export const classCategoryListSchema = z.object({
  data: z.array(classCategorySchema),
});

export const entrySheetSchema = z.object({
  data: z.object({
    projectId: z.coerce.number(),
    projectName: z.string(),
    isOpen: z.boolean(),
    classCategories: z.array(classCategorySchema),
    institutes: z.array(
      z.object({
        instituteId: z.coerce.number(),
        instituteCode: z.coerce.number().nullable(),
        instituteName: z.string(),
        thanaName: z.string().nullable(),
        counts: z.array(
          z.object({
            id: z.coerce.number(),
            classId: z.coerce.number(),
            classGroup: z.string(),
            studentQty: z.coerce.number(),
          }),
        ),
        total: z.coerce.number(),
      }),
    ),
    total: z.coerce.number(),
  }),
});

export const studentCountsSavedSchema = z.object({
  data: z.object({
    stored: z.coerce.number(),
    created: z.coerce.number(),
    updated: z.coerce.number(),
    studentQty: z.coerce.number(),
  }),
});

export const studentCountReportSchema = z.object({
  data: z.array(
    z.object({
      instituteName: z.string(),
      className: z.string(),
      classGroup: z.string(),
      studentQty: z.coerce.number(),
    }),
  ),
  meta: paginationMetaSchema.extend({
    totals: z.object({ students: z.coerce.number() }),
  }),
});

export const studentProjectSearchSchema = z.object({
  search: z.string().optional(),
  isActive: optionalFlag,
});

export const entrySheetSearchSchema = z.object({
  instituteTypeId: optionalId,
  thanaId: optionalId,
});

export interface SaveStudentProjectPayload {
  name: string;
  code?: string | undefined;
  startDate?: string | undefined;
  deadlineDate?: string | undefined;
  comments?: string | undefined;
  isActive?: boolean | undefined;
}

export interface StudentCountLine {
  instituteId: number;
  classId: number;
  classGroup: string;
  studentQty: number;
}

/* --------------------------- booklisted institution ------------------------ */

export const bookProjectSchema = z.object({
  id: z.coerce.number(),
  code: z.string().nullable(),
  name: z.string(),
  startDate: z.string().nullable(),
  deadlineDate: z.string().nullable(),
  comments: z.string().nullable(),
  isActive: z.boolean(),
  instituteCount: z.coerce.number(),
  answerCount: z.coerce.number(),
});

export const bookProjectResponseSchema = z.object({
  data: z.array(bookProjectSchema),
  meta: paginationMetaSchema.extend({
    totals: z.object({ answers: z.coerce.number() }),
  }),
});

const booklistAnswersSchema = z.object({
  id: z.coerce.number(),
  isSpDistributed: z.boolean(),
  isBookListed: z.boolean(),
  isLectureSaleFull: z.boolean(),
  isLectureSalePartial: z.boolean(),
  isOtherBookSale: z.boolean(),
  isNotWorking: z.boolean(),
  isReview: z.boolean(),
  isApprovedLevelOne: z.boolean(),
  isApprovedLevelTwo: z.boolean(),
});

export const booklistSheetSchema = z.object({
  data: z.object({
    projectId: z.coerce.number(),
    projectName: z.string(),
    isOpen: z.boolean(),
    bookEditionId: z.coerce.number(),
    answered: z.coerce.number(),
    institutes: z.array(
      z.object({
        instituteId: z.coerce.number(),
        instituteCode: z.coerce.number().nullable(),
        instituteName: z.string(),
        thanaName: z.string().nullable(),
        answers: booklistAnswersSchema.nullable(),
      }),
    ),
  }),
});

export const booklistSavedSchema = z.object({
  data: z.object({
    stored: z.coerce.number(),
    created: z.coerce.number(),
    updated: z.coerce.number(),
    locked: z.coerce.number(),
  }),
});

export const booklistAnswerRowSchema = z.object({
  id: z.coerce.number(),
  instituteId: z.coerce.number(),
  instituteName: z.string(),
  instituteTypeName: z.string().nullable(),
  thanaName: z.string().nullable(),
  bookEditionName: z.string(),
  memberId: z.coerce.number(),
  memberName: z.string().nullable(),
  isSpDistributed: z.boolean(),
  isBookListed: z.boolean(),
  isLectureSaleFull: z.boolean(),
  isLectureSalePartial: z.boolean(),
  isOtherBookSale: z.boolean(),
  isNotWorking: z.boolean(),
  isReview: z.boolean(),
  isApprovedLevelOne: z.boolean(),
  isApprovedLevelTwo: z.boolean(),
});

export const booklistAnswersResponseSchema = z.object({
  data: z.array(booklistAnswerRowSchema),
  meta: paginationMetaSchema.extend({
    totals: z.object({
      spDistributed: z.coerce.number(),
      bookListed: z.coerce.number(),
      lectureSaleFull: z.coerce.number(),
      lectureSalePartial: z.coerce.number(),
      otherBookSale: z.coerce.number(),
      notWorking: z.coerce.number(),
    }),
  }),
});

export const BOOKLIST_QUESTIONS = [
  { key: 'isSpDistributed', label: 'Specimen Distributed' },
  { key: 'isBookListed', label: 'Book Listed' },
  { key: 'isLectureSaleFull', label: 'Lecture Sale 100%' },
  { key: 'isLectureSalePartial', label: 'Lecture Sale Below 100%' },
  { key: 'isOtherBookSale', label: 'Other Book Sale' },
  { key: 'isNotWorking', label: 'Not Working' },
] as const;

export type BooklistQuestionKey = (typeof BOOKLIST_QUESTIONS)[number]['key'];

export const booklistSheetSearchSchema = z.object({
  bookEditionId: optionalId,
  instituteTypeId: optionalId,
  thanaId: optionalId,
});

export const booklistAnswersSearchSchema = z.object({
  bookEditionId: optionalId,
  instituteTypeId: optionalId,
  thanaId: optionalId,
  answeredYes: z.string().optional(),
  isReview: optionalFlag,
});

export interface BooklistAnswerLine {
  instituteId: number;
  isSpDistributed?: boolean | undefined;
  isBookListed?: boolean | undefined;
  isLectureSaleFull?: boolean | undefined;
  isLectureSalePartial?: boolean | undefined;
  isOtherBookSale?: boolean | undefined;
  isNotWorking?: boolean | undefined;
}

/* ---------------------------------- types --------------------------------- */

export type RequisitionSheet = z.infer<typeof requisitionSheetSchema>['data'];
export type RequisitionSheetInstitute = z.infer<typeof requisitionSheetInstituteSchema>;
export type RequisitionSheetSearch = z.infer<typeof requisitionSheetSearchSchema>;
export type SpecimenRequisitionRow = z.infer<typeof specimenRequisitionRowSchema>;
export type SpecimenRequisitionResponse = z.infer<typeof specimenRequisitionResponseSchema>;
export type RequisitionReviewSearch = z.infer<typeof requisitionReviewSearchSchema>;
export type SpPromoRequisitionRow = z.infer<typeof spPromoRequisitionRowSchema>;
export type SpPromoRequisitionResponse = z.infer<typeof spPromoRequisitionResponseSchema>;
export type SpPromoRequisitionMember = z.infer<typeof spPromoRequisitionMemberSchema>;
export type SpPromoRequisitionSearch = z.infer<typeof spPromoRequisitionSearchSchema>;
export type StudentProject = z.infer<typeof studentProjectSchema>;
export type StudentProjectResponse = z.infer<typeof studentProjectResponseSchema>;
export type StudentProjectSearch = z.infer<typeof studentProjectSearchSchema>;
export type ClassCategory = z.infer<typeof classCategorySchema>;
export type EntrySheet = z.infer<typeof entrySheetSchema>['data'];
export type EntrySheetSearch = z.infer<typeof entrySheetSearchSchema>;
export type StudentCountReport = z.infer<typeof studentCountReportSchema>;
export type BookProject = z.infer<typeof bookProjectSchema>;
export type BookProjectResponse = z.infer<typeof bookProjectResponseSchema>;
export type BooklistSheet = z.infer<typeof booklistSheetSchema>['data'];
export type BooklistSheetSearch = z.infer<typeof booklistSheetSearchSchema>;
export type BooklistAnswerRow = z.infer<typeof booklistAnswerRowSchema>;
export type BooklistAnswersResponse = z.infer<typeof booklistAnswersResponseSchema>;
export type BooklistAnswersSearch = z.infer<typeof booklistAnswersSearchSchema>;
